// Marks a symbolic reference to a template proposed in the SAME generation
// submission (not yet persisted) — e.g. "new:researcher". Shared by the flow
// generator (backend) and the flow editor (frontend) so both sides resolve
// AGENT_TASK.agentTemplateIds/primaryAgentTemplateId the same way.
export const NEW_AGENT_TEMPLATE_REF_PREFIX = 'new:';

/** A template with no stored value is ACTIVE — DRAFT only exists from the
 *  in-item agent-setup wizard (README-criar-agente-no-item.md §6 "Your admin
 *  isn't connected yet": the agent is saved as a draft and resumes once the
 *  connection the job needs actually exists). Every pre-existing row and
 *  every template created outside that wizard is therefore ACTIVE without
 *  ever writing the field. */
export enum AgentTemplateStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
}

/** Whether a tool the agent-setup wizard turned on can only read or can also
 *  change data — drives the "View only" / "Changes data" badge and whether
 *  the tool call needs approval (README-criar-agente-no-item.md §6). Never
 *  stored per tool on the connection itself (that stays the server's own
 *  readOnlyHint/destructiveHint classification); this is the effect the
 *  template's own tool policy was granted under. */
export enum AgentToolEffect {
  READ = 'READ',
  WRITE = 'WRITE',
}

/** A per-request ceiling on one tool's quantity param (e.g. {tool:
 *  "credits.add", param: "amount", max: 500}) — chosen by the user in the
 *  wizard's Step 2, enforced by the backend regardless of what the model
 *  requests. `tool`/`param` are structural identifiers (a wire tool name and
 *  its argument name), not user-written content — readable at rest. */
export interface AgentToolPolicyPerToolLimit {
  tool: string;
  param: string;
  max: number;
}

/** The allowlist + limits an agent template was granted for one MCP
 *  connection — the structural result of the wizard's Step 2, never the raw
 *  MCP tool metadata itself. `connectionId` and `allowedTools` are ids/wire
 *  tool names, readable at rest. */
export interface AgentToolPolicy {
  connectionId: string;
  allowedTools: string[];
  limits: AgentToolPolicyPerToolLimit[];
}

/** Owner-set: whether this agent's run asks before acting (ASK, the default), applies every
 *  action except sending outside and deleting (SKIP), or also sends to the destinations the owner
 *  saved and creates calendar events without a card (AUTONOMOUS). Applies only to the owner's own
 *  runs: a teammate running a shared agent always gets ASK. A structural enum the executor
 *  branches on, not user content, so it stays readable at rest. */
export enum AgentApprovalMode {
  ASK = 'ASK',
  SKIP = 'SKIP',
  AUTONOMOUS = 'AUTONOMOUS',
}

/** Owner-set: who a Slack message the agent sends appears to come from. A structural enum the
 *  engine branches on, not user content, so it stays readable at rest. */
export enum AgentOutboundSender {
  BOT = 'BOT',
  USER = 'USER',
}

export enum AgentOutboundPlatform {
  SLACK = 'SLACK',
}

export enum AgentOutboundDestinationKind {
  CHANNEL = 'CHANNEL',
  DIRECT_MESSAGE = 'DIRECT_MESSAGE',
}

/** `key` is server-generated and opaque: it is what the run model is allowed to pass, never a Slack id. */
export interface AgentOutboundDestination {
  key: string;
  platform: AgentOutboundPlatform;
  workspaceId: string;
  channelId: string;
  kind: AgentOutboundDestinationKind;
}

/** An email address the owner lets the agent write to. `key` is server-generated and opaque:
 *  it is what the run model passes, never the address. `address` is lower-cased and trimmed;
 *  `label` is the owner's own display name for it. Both are PII and live only in the sealed blob. */
export interface AgentOutboundEmailRecipient {
  key: string;
  address: string;
  label?: string;
}

/** Owner-set: who an agent may email besides the owner's saved recipients. SAVED_ONLY (the
 *  default for a row without it) is the saved list alone; TASK_PARTICIPANTS also lets it write to
 *  the people involved in the task it is working on. A structural enum the engine branches on,
 *  so it stays readable at rest, outside the sealed blob. */
export enum AgentRecipientScope {
  SAVED_ONLY = 'SAVED_ONLY',
  TASK_PARTICIPANTS = 'TASK_PARTICIPANTS',
}

/** Which people an agent may message by name through the people directory, beyond its saved
 *  destinations. NONE (the default for a row without it) is nobody. TEAMMATES lets it message
 *  a member of one of the owner's teams; TEAMMATES_AND_CONTACTS also lets it reach the owner's
 *  other contacts, always after the owner approves. A structural enum the engine branches on,
 *  so it stays readable at rest, outside the sealed blob. */
export enum AgentSlackPeopleScope {
  NONE = 'NONE',
  TEAMMATES = 'TEAMMATES',
  TEAMMATES_AND_CONTACTS = 'TEAMMATES_AND_CONTACTS',
}

/** Owner-chosen places an agent may send NEW messages to. Everything except `sender` and
 *  `recipientScope` is sealed at rest as one JSON blob (`outboundJson`); names of channels and
 *  people are never stored. The lists may all be empty: with TASK_PARTICIPANTS that is a valid
 *  config. */
export interface AgentOutbound {
  sender: AgentOutboundSender;
  /** Absent (a row saved before it existed) means SAVED_ONLY. */
  recipientScope?: AgentRecipientScope;
  /** Absent (a row saved before it existed) means NONE. */
  slackPeopleScope?: AgentSlackPeopleScope;
  destinations: AgentOutboundDestination[];
  mentionableUserIds: string[];
  allowBroadcastMentions: boolean;
  emailRecipients: AgentOutboundEmailRecipient[];
}

/** What the GraphQL type AgentOutbound serves: `destinations`, `mentionableUserIds` and
 *  `emailRecipients` are empty for a non-owner, so the two counts are all they can read. */
export type AgentOutboundView = AgentOutbound & {
  destinationCount: number;
  emailRecipientCount: number;
};

export interface AgentOutboundDestinationInput {
  platform: AgentOutboundPlatform;
  workspaceId: string;
  channelId: string;
  kind: AgentOutboundDestinationKind;
}

export interface AgentOutboundEmailRecipientInput {
  address: string;
  label?: string;
}

export interface AgentOutboundInput {
  /** Required when `destinations` is not empty; otherwise the saved sender is kept, or BOT. */
  sender?: AgentOutboundSender;
  /** undefined keeps the stored scope (SAVED_ONLY when none). */
  recipientScope?: AgentRecipientScope;
  /** undefined keeps the stored scope (NONE when none). */
  slackPeopleScope?: AgentSlackPeopleScope;
  destinations: AgentOutboundDestinationInput[];
  mentionableUserIds?: string[];
  allowBroadcastMentions?: boolean;
  /** undefined keeps the stored recipients (an older client that does not know the field must not
   *  wipe them); an array replaces them, an address already saved keeps its key. */
  emailRecipients?: AgentOutboundEmailRecipientInput[];
}

/** How a wait over several recipients is satisfied: every one of them replied, or any one did. */
export enum AgentReplyWaitRule {
  ALL = 'ALL',
  ANY = 'ANY',
}

/** Owner-set: whether the agent may ask a question and wait for the reply before carrying on.
 *  Holds no content and no identity, only numbers and flags the engine branches on, so it stays
 *  readable at rest. Who was asked lives in the sealed REPLY_WAIT ledger row, never here. */
export interface AgentReplyWait {
  enabled: boolean;
  maxWaitHours: number;
  remind: boolean;
  rule: AgentReplyWaitRule;
}

/** Omitted fields take the defaults (48h, remind on, rule ALL). */
export interface AgentReplyWaitInput {
  enabled: boolean;
  maxWaitHours?: number;
  remind?: boolean;
  rule?: AgentReplyWaitRule;
}

export interface AgentTemplate {
  templateId: string;
  userId: string;
  teamId?: string;
  name: string;
  description: string;
  systemPrompt: string;
  toolSets: string[];
  mcpConnectionIds?: string[];
  modelTier: string;
  maxTurns: number;
  icon?: string;
  isSystem?: boolean;
  isBoardAgent?: boolean;
  /** JSON-serialized structured-output schema (same shape as AgentTaskStepConfig.outputSchema). Applied when the template is used as a primary agent in an Agent Task step. */
  outputSchema?: string;
  createdFromTemplateId?: string;
  /** Plain-language description of the work, from the agent-setup wizard's
   *  Step 1 ("What should it do?") — user-written content, sealed at rest
   *  (AgentTemplateContentFields in kms.ts, same AAD as name/description/
   *  systemPrompt). Absent for templates created outside the wizard. */
  job?: string;
  /** Absent means ACTIVE — see AgentTemplateStatus. */
  status?: AgentTemplateStatus;
  /** Per-connection allowlist + limits granted by the agent-setup wizard.
   *  Distinct from mcpConnectionIds (connection-level, pre-existing): this
   *  is the tool-level grant the wizard's Step 2 produces. */
  toolPolicies?: AgentToolPolicy[];
  /** Whether a WRITE-effect tool call must go through approval before it
   *  runs. Always true in this version of the wizard (toggle rendered ON
   *  and locked) — the field exists so the backend guardrail has something
   *  to read rather than hardcoding the policy at the call site. */
  approvalRequiredForWrites?: boolean;
  /** Absent means ASK. Only the template's owner may change it. */
  approvalMode?: AgentApprovalMode;
  /** Absent means the agent cannot send new Slack messages. Only the template's owner may change it. */
  outbound?: AgentOutbound;
  /** Absent means the agent never waits for a reply. Only the template's owner may change it. */
  replyWait?: AgentReplyWait;
  /** The wizard verified that the only person this agent messages is its owner. A boolean the
   *  engine branches on, so it stays readable at rest. */
  notifiesOwnerOnly?: boolean;
  /** The DoBox entry this template was created from, when it was created by
   *  the in-item agent-setup wizard — ids only, same shape as
   *  AgentConversation.sourceInboxEntryRef (agent.ts). Absent for a template
   *  created any other way. */
  createdFromEntryRef?: { taskGroupId: string; entryId: string };
  createdAt: number;
  updatedAt: number;
}

export interface CreateAgentTemplateInput {
  name: string;
  teamId?: string;
  description: string;
  systemPrompt: string;
  toolSets: string[];
  mcpConnectionIds?: string[];
  modelTier: string;
  maxTurns: number;
  icon?: string;
  isBoardAgent?: boolean;
  outputSchema?: string;
  createdFromTemplateId?: string;
  status?: AgentTemplateStatus;
  toolPolicies?: AgentToolPolicy[];
  approvalRequiredForWrites?: boolean;
  createdFromEntryRef?: { taskGroupId: string; entryId: string };
  outbound?: AgentOutboundInput;
  replyWait?: AgentReplyWaitInput;
}

export interface UpdateAgentTemplateInput {
  name?: string;
  teamId?: string | null;
  description?: string;
  systemPrompt?: string;
  toolSets?: string[];
  mcpConnectionIds?: string[];
  modelTier?: string;
  maxTurns?: number;
  icon?: string;
  isBoardAgent?: boolean;
  outputSchema?: string;
  status?: AgentTemplateStatus;
  toolPolicies?: AgentToolPolicy[];
  approvalRequiredForWrites?: boolean;
  approvalMode?: AgentApprovalMode;
  /** undefined keeps the stored value; null clears it. Owner-only. */
  outbound?: AgentOutboundInput | null;
  /** undefined keeps the stored value; null clears it. Owner-only. */
  replyWait?: AgentReplyWaitInput | null;
}

export const AGENT_TASK_TEMPLATE_CONFIG_KEY = {
  PRIMARY: 'primaryAgentTemplateId',
  SUB_AGENTS: 'agentTemplateIds',
} as const;

/** PRIMARY fails the step when the template is gone; SUB_AGENT is silently dropped. */
export enum FlowAgentTemplateUsage {
  PRIMARY = 'PRIMARY',
  SUB_AGENT = 'SUB_AGENT',
}
